import { useState, useCallback, useEffect } from 'react';
import {
  Plus,
  Pencil,
  Trash2,
  Eye,
  EyeOff,
  ChevronDown,
  ChevronUp,
  X,
  Newspaper,
  Trophy,
  GraduationCap,
  Calendar,
  Clock,
  AlertCircle,
  Check,
  RefreshCw,
  Code2,
  FileText,
  FlaskConical,
  Zap,
  Trash,
  Upload,
  ImageIcon,
} from 'lucide-react';

// ─────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────

interface FeedItem {
  id: number;
  type: string;
  payload: string;
  schemaVersion: number;
  sortOrder: number;
  active: boolean;
  publishAt: string | null;
  expireAt: string | null;
  createdBy: string | null;
  updatedBy: string | null;
  createdAt: string;
  updatedAt: string | null;
  displayTitle: string | null;
}

interface PageResponse {
  content: FeedItem[];
  totalElements: number;
  totalPages: number;
  number: number;
  size: number;
}

type FeedType = 'buzz' | 'score_highlight' | 'provider_spotlight' | 'event';
type EditorTab = 'form' | 'json' | 'mock';

const FEED_TYPES: { value: FeedType; label: string; icon: typeof Newspaper }[] = [
  { value: 'buzz', label: 'Buzz', icon: Newspaper },
  { value: 'score_highlight', label: 'Score Highlight', icon: Trophy },
  { value: 'provider_spotlight', label: 'Provider Spotlight', icon: GraduationCap },
  { value: 'event', label: 'Event', icon: Calendar },
];

const SPORT_OPTIONS = ['tennis', 'pickleball'];
const HIGHLIGHT_TYPES = ['match_result', 'tournament', 'milestone', 'streak'];
const MATCH_FORMATS = ['Singles', 'Doubles', 'Mixed Doubles'];
const PROVIDER_TYPES = ['coach', 'stringer', 'academy'];
const EVENT_TYPES = ['session', 'tournament', 'clinic', 'mixer'];
const EVENT_STATUSES = ['upcoming', 'in_progress', 'completed', 'cancelled'];
const SKILL_LEVELS = ['All Levels', 'Beginner', 'Intermediate', 'Advanced', '3.0+', '3.5+', '4.0+', '4.5+'];
const BUZZ_CATEGORIES = ['news', 'highlight', 'tournament', 'opinion', 'gear'];

// ─────────────────────────────────────────────────────────────
// Mock sample data — 8 per type, filtered by selected type
// ─────────────────────────────────────────────────────────────

interface MockSample { type: FeedType; label: string; subtitle: string; payload: Record<string, unknown> }

const MOCK_SAMPLES: MockSample[] = [
  // ── Buzz (8) ──────────────────────────────────────────────
  {
    type: 'buzz', label: 'Djokovic Wins Record Slam', subtitle: 'Tennis.com • News',
    payload: {
      headline: 'Djokovic Clinches Record 25th Grand Slam Title',
      snippet: 'Novak Djokovic defeated Carlos Alcaraz in a five-set thriller to claim his 25th major title at the Australian Open.',
      source: 'Tennis.com', imageUrl: null, sports: ['tennis'], category: 'news',
      publishedAt: '2026-01-28T14:30:00Z', timeAgo: '2h ago', reactionCount: 342, commentCount: 87,
    },
  },
  {
    type: 'buzz', label: 'PPA Tour Expands to 30 Cities', subtitle: 'Pickleball Magazine • Tournament',
    payload: {
      headline: 'PPA Tour Announces 2026 Expansion to 30 Cities',
      snippet: 'The Professional Pickleball Association expands with new stops in Dallas, Austin, and Miami.',
      source: 'Pickleball Magazine', imageUrl: null, sports: ['pickleball'], category: 'tournament',
      publishedAt: '2026-02-01T09:00:00Z', timeAgo: '5h ago', reactionCount: 156, commentCount: 42,
    },
  },
  {
    type: 'buzz', label: 'New Racquet Tech Review', subtitle: 'Gear Weekly • Gear',
    payload: {
      headline: 'Wilson Clash 100 v3 Review: Power Meets Flexibility',
      snippet: 'The latest iteration of the Wilson Clash brings improved stability while maintaining the signature flex players love.',
      source: 'Gear Weekly', imageUrl: null, sports: ['tennis'], category: 'gear',
      publishedAt: '2026-02-03T11:00:00Z', timeAgo: '1d ago', reactionCount: 89, commentCount: 34,
    },
  },
  {
    type: 'buzz', label: 'Pickleball Olympics Confirmed', subtitle: 'ESPN • News',
    payload: {
      headline: 'Pickleball Officially Added to 2028 LA Olympics',
      snippet: 'The International Olympic Committee confirms pickleball as a demonstration sport at the 2028 Los Angeles Games.',
      source: 'ESPN', imageUrl: null, sports: ['pickleball'], category: 'news',
      publishedAt: '2026-02-05T16:00:00Z', timeAgo: '8h ago', reactionCount: 1024, commentCount: 312,
    },
  },
  {
    type: 'buzz', label: 'DFW Tennis Boom', subtitle: 'Dallas Morning News • Highlight',
    payload: {
      headline: 'DFW Tennis Court Reservations Up 40% in 2026',
      snippet: 'North Texas is seeing a massive surge in tennis participation, with municipal courts fully booked weeks in advance.',
      source: 'Dallas Morning News', imageUrl: null, sports: ['tennis'], category: 'highlight',
      publishedAt: '2026-02-04T08:30:00Z', timeAgo: '12h ago', reactionCount: 203, commentCount: 67,
    },
  },
  {
    type: 'buzz', label: 'Paddle Battle: Which to Choose?', subtitle: 'TARK Blog • Opinion',
    payload: {
      headline: 'Paddle Battle: How to Choose Your First Pickleball Paddle',
      snippet: 'Our guide breaks down weight, grip size, core material, and surface texture to help beginners find their perfect paddle.',
      source: 'TARK Blog', imageUrl: null, sports: ['pickleball'], category: 'opinion',
      publishedAt: '2026-02-06T10:00:00Z', timeAgo: '6h ago', reactionCount: 77, commentCount: 29,
    },
  },
  {
    type: 'buzz', label: 'Junior Tennis Star Rises', subtitle: 'Tennis Channel • Highlight',
    payload: {
      headline: '14-Year-Old Plano Prodigy Wins National Junior Title',
      snippet: 'Aanya Sharma from Plano dominates the USTA National Junior Championships, winning without dropping a set.',
      source: 'Tennis Channel', imageUrl: null, sports: ['tennis'], category: 'highlight',
      publishedAt: '2026-02-02T15:00:00Z', timeAgo: '3d ago', reactionCount: 456, commentCount: 128,
    },
  },
  {
    type: 'buzz', label: 'Mixed Doubles Growing Fast', subtitle: 'Racquet Sports Weekly • News',
    payload: {
      headline: 'Mixed Doubles Leagues See 60% Growth Across Texas',
      snippet: 'Both tennis and pickleball mixed doubles formats are exploding in popularity, with new leagues forming weekly across DFW.',
      source: 'Racquet Sports Weekly', imageUrl: null, sports: ['tennis', 'pickleball'], category: 'news',
      publishedAt: '2026-02-06T13:00:00Z', timeAgo: '4h ago', reactionCount: 134, commentCount: 51,
    },
  },

  // ── Score Highlights (8) ──────────────────────────────────
  {
    type: 'score_highlight', label: 'Plano Singles Thriller', subtitle: '6-4, 3-6, 7-5 • Tennis',
    payload: {
      highlightType: 'match_result', sport: 'tennis', matchFormat: 'Singles',
      title: 'Plano Tennis League — Week 8 Thriller', participants: ['Raj Patel', 'Marcus Chen'],
      scoreDisplay: '6-4, 3-6, 7-5', winner: 'Raj Patel', city: 'Plano', state: 'TX',
      venue: 'Oak Point Recreation Center', playedAt: '2026-02-06T19:00:00Z',
      timeAgo: '3h ago', reactionCount: 28, badge: 'Thriller', imageUrl: null,
    },
  },
  {
    type: 'score_highlight', label: 'Frisco Doubles Upset', subtitle: '11-7, 9-11, 11-5 • Pickleball',
    payload: {
      highlightType: 'match_result', sport: 'pickleball', matchFormat: 'Doubles',
      title: 'Frisco Friday Night Doubles', participants: ['Team Smash Bros', 'Dink Dynasty'],
      scoreDisplay: '11-7, 9-11, 11-5', winner: 'Team Smash Bros', city: 'Frisco', state: 'TX',
      venue: 'Frisco Athletic Center', playedAt: '2026-02-05T20:30:00Z',
      timeAgo: '1d ago', reactionCount: 45, badge: 'Upset', imageUrl: null,
    },
  },
  {
    type: 'score_highlight', label: 'Allen Mixed Doubles Final', subtitle: '6-3, 6-4 • Tennis',
    payload: {
      highlightType: 'match_result', sport: 'tennis', matchFormat: 'Mixed Doubles',
      title: 'Allen City Mixed Doubles Championship', participants: ['Kim & Davis', 'Rodriguez & Patel'],
      scoreDisplay: '6-3, 6-4', winner: 'Kim & Davis', city: 'Allen', state: 'TX',
      venue: 'Allen Tennis Center', playedAt: '2026-02-04T18:00:00Z',
      timeAgo: '2d ago', reactionCount: 62, badge: null, imageUrl: null,
    },
  },
  {
    type: 'score_highlight', label: 'McKinney Tournament Final', subtitle: '11-9, 11-8 • Pickleball',
    payload: {
      highlightType: 'tournament', sport: 'pickleball', matchFormat: 'Singles',
      title: 'McKinney Open — Singles Final', participants: ['Jake Morrison', 'Tyler Brooks'],
      scoreDisplay: '11-9, 11-8', winner: 'Jake Morrison', city: 'McKinney', state: 'TX',
      venue: 'Gabe Nesbitt Community Park', playedAt: '2026-02-03T16:00:00Z',
      timeAgo: '3d ago', reactionCount: 91, badge: null, imageUrl: null,
    },
  },
  {
    type: 'score_highlight', label: '50-Win Milestone', subtitle: 'Milestone • Tennis',
    payload: {
      highlightType: 'milestone', sport: 'tennis', matchFormat: 'Singles',
      title: 'Sarah Kim Reaches 50 League Wins', participants: ['Sarah Kim'],
      scoreDisplay: '50 wins', winner: 'Sarah Kim', city: 'Plano', state: 'TX',
      venue: 'Various', playedAt: '2026-02-06T12:00:00Z',
      timeAgo: '5h ago', reactionCount: 112, badge: 'Milestone', imageUrl: null,
    },
  },
  {
    type: 'score_highlight', label: '10-Game Win Streak', subtitle: 'Streak • Pickleball',
    payload: {
      highlightType: 'streak', sport: 'pickleball', matchFormat: 'Doubles',
      title: 'The Kitchen Kings Hit 10-Game Win Streak', participants: ['The Kitchen Kings'],
      scoreDisplay: '10-0 streak', winner: 'The Kitchen Kings', city: 'Richardson', state: 'TX',
      venue: 'Heights Recreation Center', playedAt: '2026-02-05T21:00:00Z',
      timeAgo: '1d ago', reactionCount: 78, badge: 'On Fire', imageUrl: null,
    },
  },
  {
    type: 'score_highlight', label: 'Richardson Doubles Classic', subtitle: '7-6, 6-2 • Tennis',
    payload: {
      highlightType: 'match_result', sport: 'tennis', matchFormat: 'Doubles',
      title: 'Richardson Doubles Classic — Semi-Final', participants: ['Nguyen & Park', 'Smith & Johnson'],
      scoreDisplay: '7-6(5), 6-2', winner: 'Nguyen & Park', city: 'Richardson', state: 'TX',
      venue: 'Huffhines Tennis Center', playedAt: '2026-02-06T17:30:00Z',
      timeAgo: '4h ago', reactionCount: 33, badge: null, imageUrl: null,
    },
  },
  {
    type: 'score_highlight', label: 'Prosper Mixed Doubles', subtitle: '11-6, 11-4 • Pickleball',
    payload: {
      highlightType: 'match_result', sport: 'pickleball', matchFormat: 'Mixed Doubles',
      title: 'Prosper Community Mixed Doubles Night', participants: ['Lisa & Tom', 'Amy & Steve'],
      scoreDisplay: '11-6, 11-4', winner: 'Lisa & Tom', city: 'Prosper', state: 'TX',
      venue: 'Prosper Recreation Center', playedAt: '2026-02-06T20:00:00Z',
      timeAgo: '1h ago', reactionCount: 19, badge: null, imageUrl: null,
    },
  },

  // ── Provider Spotlights (8) ───────────────────────────────
  {
    type: 'provider_spotlight', label: 'Coach Sarah Mitchell', subtitle: 'Coach • Plano, TX',
    payload: {
      displayName: 'Sarah Mitchell', providerType: 'coach',
      bio: 'USPTA Elite certified coach with 12+ years of experience. Specializing in competitive juniors and adult league players.',
      avatarUrl: null, sports: ['tennis'], city: 'Plano', state: 'TX',
      distanceDisplay: '1.8 mi', distanceMiles: 1.8, rating: 4.9,
      reviewCount: 73, verified: true, priceRange: '$75-95/hr', featuredBadge: 'USPTA Elite',
    },
  },
  {
    type: 'provider_spotlight', label: 'Ace Strings & Racquet Lab', subtitle: 'Stringer • Richardson, TX',
    payload: {
      displayName: 'Ace Strings & Racquet Lab', providerType: 'stringer',
      bio: 'Same-day stringing with professional-grade machines. Serving the DFW tennis and pickleball community since 2019.',
      avatarUrl: null, sports: ['tennis', 'pickleball'], city: 'Richardson', state: 'TX',
      distanceDisplay: '3.2 mi', distanceMiles: 3.2, rating: 4.7,
      reviewCount: 128, verified: true, priceRange: '$25-45', featuredBadge: null,
    },
  },
  {
    type: 'provider_spotlight', label: 'DFW Pickleball Academy', subtitle: 'Academy • Frisco, TX',
    payload: {
      displayName: 'DFW Pickleball Academy', providerType: 'academy',
      bio: 'Group clinics, private lessons, and tournament prep. All levels welcome. Indoor and outdoor courts available.',
      avatarUrl: null, sports: ['pickleball'], city: 'Frisco', state: 'TX',
      distanceDisplay: '5.1 mi', distanceMiles: 5.1, rating: 4.8,
      reviewCount: 94, verified: true, priceRange: '$40-70/hr', featuredBadge: 'Top Rated',
    },
  },
  {
    type: 'provider_spotlight', label: 'Coach David Park', subtitle: 'Coach • Allen, TX',
    payload: {
      displayName: 'David Park', providerType: 'coach',
      bio: 'Former D1 player turned coach. Focused on adult beginners and intermediate players looking to break through.',
      avatarUrl: null, sports: ['tennis'], city: 'Allen', state: 'TX',
      distanceDisplay: '4.3 mi', distanceMiles: 4.3, rating: 4.6,
      reviewCount: 41, verified: true, priceRange: '$60-80/hr', featuredBadge: null,
    },
  },
  {
    type: 'provider_spotlight', label: 'Pro Paddle Fix', subtitle: 'Stringer • McKinney, TX',
    payload: {
      displayName: 'Pro Paddle Fix', providerType: 'stringer',
      bio: 'Pickleball paddle regripping, edge guard replacement, and custom lead tape weighting. Walk-ins welcome.',
      avatarUrl: null, sports: ['pickleball'], city: 'McKinney', state: 'TX',
      distanceDisplay: '6.7 mi', distanceMiles: 6.7, rating: 4.5,
      reviewCount: 36, verified: false, priceRange: '$15-35', featuredBadge: null,
    },
  },
  {
    type: 'provider_spotlight', label: 'North Texas Tennis Academy', subtitle: 'Academy • Plano, TX',
    payload: {
      displayName: 'North Texas Tennis Academy', providerType: 'academy',
      bio: 'Premier tennis training for juniors and adults. USTA team training, cardio tennis, and private coaching available.',
      avatarUrl: null, sports: ['tennis'], city: 'Plano', state: 'TX',
      distanceDisplay: '2.1 mi', distanceMiles: 2.1, rating: 4.8,
      reviewCount: 156, verified: true, priceRange: '$50-120/hr', featuredBadge: 'USTA Partner',
    },
  },
  {
    type: 'provider_spotlight', label: 'Coach Maria Rodriguez', subtitle: 'Coach • Frisco, TX',
    payload: {
      displayName: 'Maria Rodriguez', providerType: 'coach',
      bio: 'Bilingual coach (English/Spanish). Specializing in pickleball strategy and third-shot drop techniques.',
      avatarUrl: null, sports: ['pickleball'], city: 'Frisco', state: 'TX',
      distanceDisplay: '4.8 mi', distanceMiles: 4.8, rating: 4.7,
      reviewCount: 52, verified: true, priceRange: '$55-75/hr', featuredBadge: 'PPR Certified',
    },
  },
  {
    type: 'provider_spotlight', label: 'Collin County Racquet Services', subtitle: 'Stringer • Plano, TX',
    payload: {
      displayName: 'Collin County Racquet Services', providerType: 'stringer',
      bio: 'Expert stringing for tennis and pickleball. 24-hour turnaround guaranteed. Demo racquets available.',
      avatarUrl: null, sports: ['tennis', 'pickleball'], city: 'Plano', state: 'TX',
      distanceDisplay: '1.5 mi', distanceMiles: 1.5, rating: 4.9,
      reviewCount: 203, verified: true, priceRange: '$20-50', featuredBadge: null,
    },
  },

  // ── Events (8) ────────────────────────────────────────────
  {
    type: 'event', label: 'Plano Doubles Mixer', subtitle: 'Sat, Feb 14 • Tennis',
    payload: {
      title: 'Plano Saturday Doubles Mixer', sport: 'tennis', eventType: 'mixer',
      date: '2026-02-14', startTime: '09:00:00', dateTimeDisplay: 'Sat, Feb 14 at 9:00 AM',
      venue: 'High Point Tennis Center', city: 'Plano', state: 'TX',
      locationDisplay: 'High Point, Plano TX', spotsFilledCount: 6, capacity: 8,
      spotsDisplay: '2 spots left', skillLevel: '3.5+', isPublic: true, status: 'upcoming', imageUrl: null,
    },
  },
  {
    type: 'event', label: 'Beginner Pickleball Clinic', subtitle: 'Sun, Feb 15 • Pickleball',
    payload: {
      title: 'Beginner Pickleball Clinic — Learn the Basics', sport: 'pickleball', eventType: 'clinic',
      date: '2026-02-15', startTime: '10:00:00', dateTimeDisplay: 'Sun, Feb 15 at 10:00 AM',
      venue: 'Allen Community Center', city: 'Allen', state: 'TX',
      locationDisplay: 'Allen Community Center, Allen TX', spotsFilledCount: 12, capacity: 20,
      spotsDisplay: '8 spots left', skillLevel: 'Beginner', isPublic: true, status: 'upcoming', imageUrl: null,
    },
  },
  {
    type: 'event', label: 'Frisco Open Tournament', subtitle: 'Feb 21-22 • Tennis',
    payload: {
      title: 'Frisco Open — Singles & Doubles Tournament', sport: 'tennis', eventType: 'tournament',
      date: '2026-02-21', startTime: '08:00:00', dateTimeDisplay: 'Sat-Sun, Feb 21-22',
      venue: 'Frisco Tennis Center', city: 'Frisco', state: 'TX',
      locationDisplay: 'Frisco Tennis Center, Frisco TX', spotsFilledCount: 28, capacity: 32,
      spotsDisplay: '4 spots left', skillLevel: '4.0+', isPublic: true, status: 'upcoming', imageUrl: null,
    },
  },
  {
    type: 'event', label: 'McKinney Pickleball Social', subtitle: 'Fri, Feb 20 • Pickleball',
    payload: {
      title: 'Friday Night Pickleball Social', sport: 'pickleball', eventType: 'mixer',
      date: '2026-02-20', startTime: '18:30:00', dateTimeDisplay: 'Fri, Feb 20 at 6:30 PM',
      venue: 'Gabe Nesbitt Community Park', city: 'McKinney', state: 'TX',
      locationDisplay: 'Gabe Nesbitt Park, McKinney TX', spotsFilledCount: 10, capacity: 16,
      spotsDisplay: '6 spots left', skillLevel: 'All Levels', isPublic: true, status: 'upcoming', imageUrl: null,
    },
  },
  {
    type: 'event', label: 'Advanced Strategy Clinic', subtitle: 'Sat, Feb 28 • Tennis',
    payload: {
      title: 'Advanced Tennis Strategy Clinic with Coach Mitchell', sport: 'tennis', eventType: 'clinic',
      date: '2026-02-28', startTime: '14:00:00', dateTimeDisplay: 'Sat, Feb 28 at 2:00 PM',
      venue: 'Oak Point Recreation Center', city: 'Plano', state: 'TX',
      locationDisplay: 'Oak Point, Plano TX', spotsFilledCount: 3, capacity: 6,
      spotsDisplay: '3 spots left', skillLevel: '4.0+', isPublic: true, status: 'upcoming', imageUrl: null,
    },
  },
  {
    type: 'event', label: 'Richardson Round Robin', subtitle: 'Sun, Feb 22 • Pickleball',
    payload: {
      title: 'Richardson Pickleball Round Robin', sport: 'pickleball', eventType: 'session',
      date: '2026-02-22', startTime: '09:00:00', dateTimeDisplay: 'Sun, Feb 22 at 9:00 AM',
      venue: 'Heights Recreation Center', city: 'Richardson', state: 'TX',
      locationDisplay: 'Heights Rec, Richardson TX', spotsFilledCount: 14, capacity: 16,
      spotsDisplay: '2 spots left', skillLevel: '3.5+', isPublic: true, status: 'upcoming', imageUrl: null,
    },
  },
  {
    type: 'event', label: 'Allen Mixed Doubles League', subtitle: 'Every Wed • Tennis',
    payload: {
      title: 'Allen Wednesday Mixed Doubles League', sport: 'tennis', eventType: 'session',
      date: '2026-02-19', startTime: '19:00:00', dateTimeDisplay: 'Wed, Feb 19 at 7:00 PM',
      venue: 'Allen Tennis Center', city: 'Allen', state: 'TX',
      locationDisplay: 'Allen Tennis Center, Allen TX', spotsFilledCount: 7, capacity: 8,
      spotsDisplay: '1 spot left', skillLevel: '3.5+', isPublic: true, status: 'upcoming', imageUrl: null,
    },
  },
  {
    type: 'event', label: 'Prosper Charity Tournament', subtitle: 'Mar 1 • Pickleball',
    payload: {
      title: 'Prosper Charity Pickleball Tournament — All Levels', sport: 'pickleball', eventType: 'tournament',
      date: '2026-03-01', startTime: '08:30:00', dateTimeDisplay: 'Sun, Mar 1 at 8:30 AM',
      venue: 'Prosper Recreation Center', city: 'Prosper', state: 'TX',
      locationDisplay: 'Prosper Rec Center, Prosper TX', spotsFilledCount: 18, capacity: 48,
      spotsDisplay: '30 spots left', skillLevel: 'All Levels', isPublic: true, status: 'upcoming', imageUrl: null,
    },
  },
];

// ─────────────────────────────────────────────────────────────
// API Helper
// ─────────────────────────────────────────────────────────────

async function fetchApi<T>(url: string, options: RequestInit = {}): Promise<T> {
  const response = await fetch(url, {
    ...options,
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...options.headers },
  });
  if (!response.ok) {
    const errorText = await response.text();
    let errorMessage = `HTTP ${response.status}`;
    try {
      const errorJson = JSON.parse(errorText);
      errorMessage = errorJson.message || errorJson.error || errorMessage;
    } catch { errorMessage = errorText || errorMessage; }
    throw new Error(errorMessage);
  }
  return response.json();
}

// ─────────────────────────────────────────────────────────────
// Default payloads per type (for new items)
// ─────────────────────────────────────────────────────────────

function getDefaultPayload(type: FeedType): Record<string, unknown> {
  const defaults: Record<FeedType, Record<string, unknown>> = {
    buzz: {
      headline: '', snippet: '', source: '', imageUrl: null,
      sports: ['tennis'], category: 'news',
      publishedAt: new Date().toISOString(), timeAgo: 'Just now',
      reactionCount: 0, commentCount: 0,
    },
    score_highlight: {
      highlightType: 'match_result', sport: 'tennis', matchFormat: 'Singles',
      title: '', participants: ['Team A', 'Team B'],
      scoreDisplay: '', winner: '', city: '', state: '', venue: '',
      playedAt: new Date().toISOString(), timeAgo: 'Just now', reactionCount: 0,
    },
    provider_spotlight: {
      displayName: '', providerType: 'coach', bio: '', avatarUrl: null,
      sports: ['tennis'], city: '', state: '',
      distanceDisplay: '', distanceMiles: 0, rating: 4.5,
      reviewCount: 0, verified: false, priceRange: '',
    },
    event: {
      title: '', sport: 'tennis', eventType: 'session',
      date: new Date().toISOString().split('T')[0], startTime: '18:30:00',
      dateTimeDisplay: '', venue: '', city: '', state: '', locationDisplay: '',
      spotsFilledCount: 0, capacity: 8, spotsDisplay: '',
      skillLevel: 'All Levels', isPublic: true, status: 'upcoming',
    },
  };
  return { ...defaults[type] };
}

// ─────────────────────────────────────────────────────────────
// Reusable form input components
// ─────────────────────────────────────────────────────────────

const inputCls = 'w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:ring-cyan-500 focus:border-cyan-500';
const labelCls = 'block text-xs font-medium text-slate-500 mb-1';
const selectCls = inputCls;

function FormField({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) {
  return (
    <div>
      <label className={labelCls}>
        {label} {required && <span className="text-red-400">*</span>}
      </label>
      {children}
    </div>
  );
}

function TextInput({ value, onChange, placeholder, required }: { value: string; onChange: (v: string) => void; placeholder?: string; required?: boolean }) {
  return <input type="text" value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} required={required} className={inputCls} />;
}

function NumberInput({ value, onChange, placeholder, min, max, step }: { value: number; onChange: (v: number) => void; placeholder?: string; min?: number; max?: number; step?: number }) {
  return <input type="number" value={value} onChange={(e) => onChange(Number(e.target.value))} placeholder={placeholder} min={min} max={max} step={step} className={inputCls} />;
}

function TextAreaInput({ value, onChange, placeholder, rows }: { value: string; onChange: (v: string) => void; placeholder?: string; rows?: number }) {
  return <textarea value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} rows={rows || 2} className={inputCls + ' resize-y'} />;
}

function SelectInput({ value, onChange, options }: { value: string; onChange: (v: string) => void; options: string[] }) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} className={selectCls}>
      {options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  );
}

function CheckboxInput({ label, checked, onChange }: { label: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <label className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="rounded border-slate-600 bg-slate-800 text-cyan-500 focus:ring-cyan-500" />
      {label}
    </label>
  );
}

function MultiSportSelect({ value, onChange }: { value: string[]; onChange: (v: string[]) => void }) {
  const toggle = (sport: string) => {
    if (value.includes(sport)) {
      if (value.length > 1) onChange(value.filter((s) => s !== sport));
    } else {
      onChange([...value, sport]);
    }
  };
  return (
    <div className="flex gap-2">
      {SPORT_OPTIONS.map((s) => (
        <button
          key={s}
          type="button"
          onClick={() => toggle(s)}
          className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
            value.includes(s)
              ? 'border-cyan-500 bg-cyan-500/10 text-cyan-400'
              : 'border-slate-700 bg-slate-800 text-slate-500 hover:border-slate-500'
          }`}
        >
          {s}
        </button>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Type-specific form renderers
// ─────────────────────────────────────────────────────────────

function BuzzForm({ data, onChange }: { data: Record<string, unknown>; onChange: (d: Record<string, unknown>) => void }) {
  const set = (key: string, val: unknown) => onChange({ ...data, [key]: val });
  return (
    <div className="space-y-4">
      <FormField label="Headline" required><TextInput value={(data.headline as string) || ''} onChange={(v) => set('headline', v)} placeholder="e.g. Djokovic Wins Australian Open" /></FormField>
      <FormField label="Snippet"><TextAreaInput value={(data.snippet as string) || ''} onChange={(v) => set('snippet', v)} placeholder="Short preview text..." /></FormField>
      <div className="grid grid-cols-2 gap-4">
        <FormField label="Source" required><TextInput value={(data.source as string) || ''} onChange={(v) => set('source', v)} placeholder="e.g. Tennis.com" /></FormField>
        <FormField label="Category"><SelectInput value={(data.category as string) || 'news'} onChange={(v) => set('category', v)} options={BUZZ_CATEGORIES} /></FormField>
      </div>
      <FormField label="Sports" required><MultiSportSelect value={(data.sports as string[]) || ['tennis']} onChange={(v) => set('sports', v)} /></FormField>
      <FormField label="Image URL"><TextInput value={(data.imageUrl as string) || ''} onChange={(v) => set('imageUrl', v || null)} placeholder="https://cdn.playtark.com/..." /></FormField>
      <div className="grid grid-cols-3 gap-4">
        <FormField label="Time Ago"><TextInput value={(data.timeAgo as string) || ''} onChange={(v) => set('timeAgo', v)} placeholder="2h ago" /></FormField>
        <FormField label="Reactions"><NumberInput value={(data.reactionCount as number) || 0} onChange={(v) => set('reactionCount', v)} min={0} /></FormField>
        <FormField label="Comments"><NumberInput value={(data.commentCount as number) || 0} onChange={(v) => set('commentCount', v)} min={0} /></FormField>
      </div>
    </div>
  );
}

function HighlightForm({ data, onChange }: { data: Record<string, unknown>; onChange: (d: Record<string, unknown>) => void }) {
  const set = (key: string, val: unknown) => onChange({ ...data, [key]: val });
  const participants = (data.participants as string[]) || ['', ''];
  const setParticipant = (idx: number, val: string) => {
    const copy = [...participants];
    copy[idx] = val;
    set('participants', copy);
  };
  return (
    <div className="space-y-4">
      <FormField label="Title" required><TextInput value={(data.title as string) || ''} onChange={(v) => set('title', v)} placeholder="e.g. Local Tennis Highlight" /></FormField>
      <div className="grid grid-cols-3 gap-4">
        <FormField label="Sport" required><SelectInput value={(data.sport as string) || 'tennis'} onChange={(v) => set('sport', v)} options={SPORT_OPTIONS} /></FormField>
        <FormField label="Highlight Type"><SelectInput value={(data.highlightType as string) || 'match_result'} onChange={(v) => set('highlightType', v)} options={HIGHLIGHT_TYPES} /></FormField>
        <FormField label="Match Format"><SelectInput value={(data.matchFormat as string) || 'Singles'} onChange={(v) => set('matchFormat', v)} options={MATCH_FORMATS} /></FormField>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <FormField label="Team / Player A"><TextInput value={participants[0] || ''} onChange={(v) => setParticipant(0, v)} placeholder="Team A" /></FormField>
        <FormField label="Team / Player B"><TextInput value={participants[1] || ''} onChange={(v) => setParticipant(1, v)} placeholder="Team B" /></FormField>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <FormField label="Score" required><TextInput value={(data.scoreDisplay as string) || ''} onChange={(v) => set('scoreDisplay', v)} placeholder="6-4, 7-5" /></FormField>
        <FormField label="Winner"><TextInput value={(data.winner as string) || ''} onChange={(v) => set('winner', v)} placeholder="Team A" /></FormField>
      </div>
      <div className="grid grid-cols-3 gap-4">
        <FormField label="City"><TextInput value={(data.city as string) || ''} onChange={(v) => set('city', v)} placeholder="Plano" /></FormField>
        <FormField label="State"><TextInput value={(data.state as string) || ''} onChange={(v) => set('state', v)} placeholder="TX" /></FormField>
        <FormField label="Venue"><TextInput value={(data.venue as string) || ''} onChange={(v) => set('venue', v)} placeholder="Oak Point Rec Center" /></FormField>
      </div>
      <div className="grid grid-cols-3 gap-4">
        <FormField label="Time Ago"><TextInput value={(data.timeAgo as string) || ''} onChange={(v) => set('timeAgo', v)} placeholder="3h ago" /></FormField>
        <FormField label="Reactions"><NumberInput value={(data.reactionCount as number) || 0} onChange={(v) => set('reactionCount', v)} min={0} /></FormField>
        <FormField label="Badge"><TextInput value={(data.badge as string) || ''} onChange={(v) => set('badge', v || undefined)} placeholder="Upset, Thriller..." /></FormField>
      </div>
      <FormField label="Image URL"><TextInput value={(data.imageUrl as string) || ''} onChange={(v) => set('imageUrl', v || null)} placeholder="https://cdn.playtark.com/..." /></FormField>
    </div>
  );
}

function ProviderForm({ data, onChange }: { data: Record<string, unknown>; onChange: (d: Record<string, unknown>) => void }) {
  const set = (key: string, val: unknown) => onChange({ ...data, [key]: val });
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <FormField label="Display Name" required><TextInput value={(data.displayName as string) || ''} onChange={(v) => set('displayName', v)} placeholder="Coach Smith" /></FormField>
        <FormField label="Provider Type" required><SelectInput value={(data.providerType as string) || 'coach'} onChange={(v) => set('providerType', v)} options={PROVIDER_TYPES} /></FormField>
      </div>
      <FormField label="Bio"><TextAreaInput value={(data.bio as string) || ''} onChange={(v) => set('bio', v)} placeholder="Short bio or tagline..." /></FormField>
      <FormField label="Sports"><MultiSportSelect value={(data.sports as string[]) || ['tennis']} onChange={(v) => set('sports', v)} /></FormField>
      <div className="grid grid-cols-3 gap-4">
        <FormField label="City" required><TextInput value={(data.city as string) || ''} onChange={(v) => set('city', v)} placeholder="Plano" /></FormField>
        <FormField label="State" required><TextInput value={(data.state as string) || ''} onChange={(v) => set('state', v)} placeholder="TX" /></FormField>
        <FormField label="Distance Display"><TextInput value={(data.distanceDisplay as string) || ''} onChange={(v) => set('distanceDisplay', v)} placeholder="2.3 mi" /></FormField>
      </div>
      <div className="grid grid-cols-3 gap-4">
        <FormField label="Rating"><NumberInput value={(data.rating as number) || 4.5} onChange={(v) => set('rating', v)} min={1} max={5} step={0.1} /></FormField>
        <FormField label="Review Count"><NumberInput value={(data.reviewCount as number) || 0} onChange={(v) => set('reviewCount', v)} min={0} /></FormField>
        <FormField label="Price Range"><TextInput value={(data.priceRange as string) || ''} onChange={(v) => set('priceRange', v)} placeholder="$60-80/hr" /></FormField>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <FormField label="Avatar URL"><TextInput value={(data.avatarUrl as string) || ''} onChange={(v) => set('avatarUrl', v || null)} placeholder="https://cdn.playtark.com/..." /></FormField>
        <FormField label="Featured Badge"><TextInput value={(data.featuredBadge as string) || ''} onChange={(v) => set('featuredBadge', v || null)} placeholder="USPTA Certified" /></FormField>
      </div>
      <CheckboxInput label="Verified" checked={(data.verified as boolean) || false} onChange={(v) => set('verified', v)} />
    </div>
  );
}

function EventForm({ data, onChange }: { data: Record<string, unknown>; onChange: (d: Record<string, unknown>) => void }) {
  const set = (key: string, val: unknown) => onChange({ ...data, [key]: val });
  return (
    <div className="space-y-4">
      <FormField label="Title" required><TextInput value={(data.title as string) || ''} onChange={(v) => set('title', v)} placeholder="Plano Doubles Mixer" /></FormField>
      <div className="grid grid-cols-3 gap-4">
        <FormField label="Sport" required><SelectInput value={(data.sport as string) || 'tennis'} onChange={(v) => set('sport', v)} options={SPORT_OPTIONS} /></FormField>
        <FormField label="Event Type"><SelectInput value={(data.eventType as string) || 'session'} onChange={(v) => set('eventType', v)} options={EVENT_TYPES} /></FormField>
        <FormField label="Status"><SelectInput value={(data.status as string) || 'upcoming'} onChange={(v) => set('status', v)} options={EVENT_STATUSES} /></FormField>
      </div>
      <div className="grid grid-cols-3 gap-4">
        <FormField label="Date"><input type="date" value={(data.date as string) || ''} onChange={(e) => set('date', e.target.value)} className={inputCls} /></FormField>
        <FormField label="Start Time"><input type="time" value={(data.startTime as string) || ''} onChange={(e) => set('startTime', e.target.value)} className={inputCls} /></FormField>
        <FormField label="Date/Time Display"><TextInput value={(data.dateTimeDisplay as string) || ''} onChange={(v) => set('dateTimeDisplay', v)} placeholder="Today 6:30 PM" /></FormField>
      </div>
      <FormField label="Venue" required><TextInput value={(data.venue as string) || ''} onChange={(v) => set('venue', v)} placeholder="Oak Point Recreation Center" /></FormField>
      <div className="grid grid-cols-3 gap-4">
        <FormField label="City"><TextInput value={(data.city as string) || ''} onChange={(v) => set('city', v)} placeholder="Plano" /></FormField>
        <FormField label="State"><TextInput value={(data.state as string) || ''} onChange={(v) => set('state', v)} placeholder="TX" /></FormField>
        <FormField label="Location Display"><TextInput value={(data.locationDisplay as string) || ''} onChange={(v) => set('locationDisplay', v)} placeholder="Oak Point, Plano TX" /></FormField>
      </div>
      <div className="grid grid-cols-4 gap-4">
        <FormField label="Spots Filled"><NumberInput value={(data.spotsFilledCount as number) || 0} onChange={(v) => set('spotsFilledCount', v)} min={0} /></FormField>
        <FormField label="Capacity"><NumberInput value={(data.capacity as number) || 8} onChange={(v) => set('capacity', v)} min={1} /></FormField>
        <FormField label="Spots Display"><TextInput value={(data.spotsDisplay as string) || ''} onChange={(v) => set('spotsDisplay', v)} placeholder="Need 2 more" /></FormField>
        <FormField label="Skill Level"><SelectInput value={(data.skillLevel as string) || 'All Levels'} onChange={(v) => set('skillLevel', v)} options={SKILL_LEVELS} /></FormField>
      </div>
      <CheckboxInput label="Public event" checked={(data.isPublic as boolean) ?? true} onChange={(v) => set('isPublic', v)} />
      <FormField label="Image URL"><TextInput value={(data.imageUrl as string) || ''} onChange={(v) => set('imageUrl', v || null)} placeholder="https://cdn.playtark.com/..." /></FormField>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────────────────────

export function FeedManagementPage() {
  // Data state
  const [items, setItems] = useState<FeedItem[]>([]);
  const [totalElements, setTotalElements] = useState(0);
  const [page, setPage] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [typeFilter, setTypeFilter] = useState<string>('');
  const [activeFilter, setActiveFilter] = useState<string>('');

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState<FeedItem | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [editorTab, setEditorTab] = useState<EditorTab>('form');

  // Form state — structured data for the form tab
  const [formType, setFormType] = useState<FeedType>('buzz');
  const [formData, setFormData] = useState<Record<string, unknown>>({});
  const [formJsonRaw, setFormJsonRaw] = useState('');
  const [formSortOrder, setFormSortOrder] = useState('');
  const [formActive, setFormActive] = useState(true);
  const [formPublishAt, setFormPublishAt] = useState('');
  const [formExpireAt, setFormExpireAt] = useState('');

  // Expanded row
  const [expandedId, setExpandedId] = useState<number | null>(null);

  // ─────────────────────────────────────────────────────────
  // Sync form <-> JSON when switching tabs
  // ─────────────────────────────────────────────────────────

  const syncFormToJson = (data: Record<string, unknown>) => {
    setFormJsonRaw(JSON.stringify(data, null, 2));
  };

  const syncJsonToForm = (json: string): boolean => {
    try {
      const parsed = JSON.parse(json);
      if (typeof parsed === 'object' && parsed !== null) {
        setFormData(parsed);
        return true;
      }
    } catch { /* invalid JSON, ignore */ }
    return false;
  };

  const handleTabSwitch = (tab: EditorTab) => {
    if (tab === 'json' && editorTab === 'form') {
      syncFormToJson(formData);
    } else if (tab === 'form' && editorTab === 'json') {
      syncJsonToForm(formJsonRaw);
    }
    // mock tab: no sync needed — it applies via click
    setEditorTab(tab);
  };

  const handleFormDataChange = (data: Record<string, unknown>) => {
    setFormData(data);
    // Keep JSON in sync while on form tab
    syncFormToJson(data);
  };

  // ─────────────────────────────────────────────────────────
  // Data Loading
  // ─────────────────────────────────────────────────────────

  const loadItems = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      params.set('page', String(page));
      params.set('size', '50');
      if (typeFilter) params.set('type', typeFilter);
      if (activeFilter) params.set('active', activeFilter);
      const data = await fetchApi<PageResponse>(`/api/v1/admin/feed-items?${params}`);
      setItems(data.content);
      setTotalElements(data.totalElements);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load feed items');
    } finally {
      setIsLoading(false);
    }
  }, [page, typeFilter, activeFilter]);

  useEffect(() => { loadItems(); }, [loadItems]);

  // ─────────────────────────────────────────────────────────
  // CRUD Actions
  // ─────────────────────────────────────────────────────────

  const openCreateModal = () => {
    setEditingItem(null);
    setFormType('buzz');
    const defaultData = getDefaultPayload('buzz');
    setFormData(defaultData);
    setFormJsonRaw(JSON.stringify(defaultData, null, 2));
    setFormSortOrder('');
    setFormActive(true);
    setFormPublishAt('');
    setFormExpireAt('');
    setEditorTab('form');
    setSubmitError(null);
    setImagePreview(null);
    setShowModal(true);
  };

  const openEditModal = (item: FeedItem) => {
    setEditingItem(item);
    setFormType(item.type as FeedType);
    try {
      const parsed = JSON.parse(item.payload);
      setFormData(parsed);
      setFormJsonRaw(JSON.stringify(parsed, null, 2));
    } catch {
      setFormData({});
      setFormJsonRaw(item.payload);
    }
    setFormSortOrder(String(item.sortOrder));
    setFormActive(item.active);
    setFormPublishAt(item.publishAt ? item.publishAt.slice(0, 16) : '');
    setFormExpireAt(item.expireAt ? item.expireAt.slice(0, 16) : '');
    setEditorTab('form');
    setSubmitError(null);
    setImagePreview(null);
    setShowModal(true);
  };

  const handleSave = async () => {
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      // Get payload from whichever tab is active
      let payload: string;
      if (editorTab === 'form' || editorTab === 'mock') {
        payload = JSON.stringify(formData);
      } else {
        JSON.parse(formJsonRaw); // validate
        payload = formJsonRaw;
      }

      const body = {
        type: formType,
        payload,
        sortOrder: formSortOrder ? parseInt(formSortOrder) : null,
        active: formActive,
        publishAt: formPublishAt || null,
        expireAt: formExpireAt || null,
      };

      if (editingItem) {
        await fetchApi(`/api/v1/admin/feed-items/${editingItem.id}`, { method: 'PUT', body: JSON.stringify(body) });
      } else {
        await fetchApi('/api/v1/admin/feed-items', { method: 'POST', body: JSON.stringify(body) });
      }

      setShowModal(false);
      loadItems();
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (item: FeedItem) => {
    if (!window.confirm(`Delete "${item.displayTitle || 'Untitled'}"? This cannot be undone.`)) return;
    try {
      await fetchApi(`/api/v1/admin/feed-items/${item.id}`, { method: 'DELETE' });
      loadItems();
    } catch (err) { setError(err instanceof Error ? err.message : 'Delete failed'); }
  };

  const handleToggle = async (item: FeedItem) => {
    try {
      await fetchApi(`/api/v1/admin/feed-items/${item.id}/toggle`, { method: 'PATCH' });
      loadItems();
    } catch (err) { setError(err instanceof Error ? err.message : 'Toggle failed'); }
  };

  // ─────────────────────────────────────────────────────────
  // Mock data actions
  // ─────────────────────────────────────────────────────────

  const [isSeedingMocks, setIsSeedingMocks] = useState(false);
  const [isDeletingAll, setIsDeletingAll] = useState(false);

  const applyMockToForm = (mock: MockSample) => {
    setFormType(mock.type);
    setFormData({ ...mock.payload });
    setFormJsonRaw(JSON.stringify(mock.payload, null, 2));
    setEditorTab('form');
  };

  const seedAllMocks = async () => {
    if (!window.confirm(`This will create ${MOCK_SAMPLES.length} sample feed items. Continue?`)) return;
    setIsSeedingMocks(true);
    setError(null);
    try {
      for (const mock of MOCK_SAMPLES) {
        await fetchApi('/api/v1/admin/feed-items', {
          method: 'POST',
          body: JSON.stringify({
            type: mock.type,
            payload: JSON.stringify(mock.payload),
            active: true,
            sortOrder: null,
            publishAt: null,
            expireAt: null,
          }),
        });
      }
      setShowModal(false);
      loadItems();
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to seed mock data');
    } finally {
      setIsSeedingMocks(false);
    }
  };

  const deleteAllItems = async () => {
    if (items.length === 0) return;
    if (!window.confirm(`Delete ALL ${totalElements} feed items? This cannot be undone.`)) return;
    setIsDeletingAll(true);
    setError(null);
    try {
      // Load all items first (in case there are more than one page)
      const allParams = new URLSearchParams();
      allParams.set('page', '0');
      allParams.set('size', '500');
      const allData = await fetchApi<PageResponse>(`/api/v1/admin/feed-items?${allParams}`);
      for (const item of allData.content) {
        await fetchApi(`/api/v1/admin/feed-items/${item.id}`, { method: 'DELETE' });
      }
      loadItems();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete all items');
    } finally {
      setIsDeletingAll(false);
    }
  };

  // ─────────────────────────────────────────────────────────
  // Image upload
  // ─────────────────────────────────────────────────────────

  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<string | null>(null);

  const imageUrlKey = formType === 'provider_spotlight' ? 'avatarUrl' : 'imageUrl';

  const currentImageUrl = (formData[imageUrlKey] as string) || null;

  const handleImageSelect = async (file: File) => {
    // Validate size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      setSubmitError('Image must be under 5 MB');
      return;
    }
    // Validate type
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      setSubmitError('Only JPEG, PNG, and WebP images are supported');
      return;
    }

    // Show preview immediately
    const previewUrl = URL.createObjectURL(file);
    setImagePreview(previewUrl);
    setIsUploading(true);
    setUploadProgress('Uploading image...');
    setSubmitError(null);

    try {
      // Upload image via backend proxy (backend uploads to S3)
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const response = await fetch('/api/v1/admin/feed-items/upload-image', {
        method: 'POST',
        credentials: 'include',
        body: formDataUpload,
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = `Upload failed: HTTP ${response.status}`;
        try {
          const errorJson = JSON.parse(errorText);
          errorMessage = errorJson.message || errorJson.error || errorMessage;
        } catch { errorMessage = errorText || errorMessage; }
        throw new Error(errorMessage);
      }

      const { cdnUrl } = await response.json() as { cdnUrl: string; s3Key: string };

      // Set the CDN URL in payload
      setUploadProgress(null);
      const updatedData = { ...formData, [imageUrlKey]: cdnUrl };
      setFormData(updatedData);
      syncFormToJson(updatedData);
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Image upload failed');
      setImagePreview(null);
    } finally {
      setIsUploading(false);
      setUploadProgress(null);
    }
  };

  const handleRemoveImage = () => {
    const updatedData = { ...formData, [imageUrlKey]: null };
    setFormData(updatedData);
    syncFormToJson(updatedData);
    setImagePreview(null);
  };

  const handleFileDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) handleImageSelect(file);
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleImageSelect(file);
    // Reset input so same file can be selected again
    e.target.value = '';
  };

  // ─────────────────────────────────────────────────────────
  // Helpers
  // ─────────────────────────────────────────────────────────

  const getTypeConfig = (type: string) =>
    FEED_TYPES.find((t) => t.value === type) || { value: type, label: type, icon: Newspaper };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '—';
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit',
      });
    } catch { return dateStr; }
  };

  const getScheduleStatus = (item: FeedItem) => {
    const now = new Date();
    if (item.publishAt && new Date(item.publishAt) > now) return 'scheduled';
    if (item.expireAt && new Date(item.expireAt) <= now) return 'expired';
    return null;
  };

  // ─────────────────────────────────────────────────────────
  // Render type-specific form
  // ─────────────────────────────────────────────────────────

  const renderTypeForm = () => {
    switch (formType) {
      case 'buzz': return <BuzzForm data={formData} onChange={handleFormDataChange} />;
      case 'score_highlight': return <HighlightForm data={formData} onChange={handleFormDataChange} />;
      case 'provider_spotlight': return <ProviderForm data={formData} onChange={handleFormDataChange} />;
      case 'event': return <EventForm data={formData} onChange={handleFormDataChange} />;
    }
  };

  // ─────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────

  return (
    <div className="p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-white">Feed Management</h1>
            <p className="text-slate-400 text-sm mt-1">
              Manage items for the marketing homepage &quot;What&apos;s happening on TARK&quot; section
            </p>
          </div>
          <div className="flex items-center gap-3">
            {totalElements > 0 && (
              <button
                onClick={deleteAllItems}
                disabled={isDeletingAll}
                className="flex items-center gap-2 px-3 py-2 text-sm text-red-400 hover:text-red-300 border border-red-500/30 rounded-lg hover:border-red-500/50 hover:bg-red-500/5 transition-colors disabled:opacity-50"
              >
                {isDeletingAll ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Trash className="h-4 w-4" />}
                {isDeletingAll ? 'Deleting...' : 'Delete All'}
              </button>
            )}
            <button onClick={loadItems} className="flex items-center gap-2 px-3 py-2 text-sm text-slate-400 hover:text-white border border-slate-700 rounded-lg hover:border-slate-500 transition-colors">
              <RefreshCw className="h-4 w-4" /> Refresh
            </button>
            <button onClick={openCreateModal} className="flex items-center gap-2 px-4 py-2 bg-cyan-600 hover:bg-cyan-500 text-white text-sm font-medium rounded-lg transition-colors">
              <Plus className="h-4 w-4" /> Add Item
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-4 mb-6">
          <select value={typeFilter} onChange={(e) => { setTypeFilter(e.target.value); setPage(0); }} className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:ring-cyan-500 focus:border-cyan-500">
            <option value="">All Types</option>
            {FEED_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
          <select value={activeFilter} onChange={(e) => { setActiveFilter(e.target.value); setPage(0); }} className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:ring-cyan-500 focus:border-cyan-500">
            <option value="">All Status</option>
            <option value="true">Active</option>
            <option value="false">Inactive</option>
          </select>
          <span className="text-sm text-slate-500 ml-auto">{totalElements} item{totalElements !== 1 ? 's' : ''}</span>
        </div>

        {/* Error Banner */}
        {error && (
          <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg flex items-center gap-2 text-red-400 text-sm">
            <AlertCircle className="h-4 w-4 flex-shrink-0" />{error}
            <button onClick={() => setError(null)} className="ml-auto text-red-400 hover:text-red-300"><X className="h-4 w-4" /></button>
          </div>
        )}

        {/* Table */}
        <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-800">
                <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider px-4 py-3 w-10">#</th>
                <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider px-4 py-3 w-36">Type</th>
                <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider px-4 py-3">Title</th>
                <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider px-4 py-3 w-20">Order</th>
                <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider px-4 py-3 w-24">Status</th>
                <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider px-4 py-3 w-36">Schedule</th>
                <th className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider px-4 py-3 w-32">Updated</th>
                <th className="text-right text-xs font-medium text-slate-500 uppercase tracking-wider px-4 py-3 w-36">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800/50">
              {isLoading ? (
                <tr><td colSpan={8} className="px-4 py-12 text-center text-slate-500"><div className="flex items-center justify-center gap-2"><RefreshCw className="h-4 w-4 animate-spin" />Loading...</div></td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan={8} className="px-4 py-12 text-center text-slate-500">No feed items found. Click &quot;Add Item&quot; to create one.</td></tr>
              ) : (
                items.map((item, index) => {
                  const typeConfig = getTypeConfig(item.type);
                  const TypeIcon = typeConfig.icon;
                  const scheduleStatus = getScheduleStatus(item);
                  const isExpanded = expandedId === item.id;
                  return (
                    <>
                      <tr key={item.id} className={`hover:bg-slate-800/50 transition-colors ${!item.active ? 'opacity-50' : ''}`}>
                        <td className="px-4 py-3 text-sm text-slate-500">{page * 50 + index + 1}</td>
                        <td className="px-4 py-3">
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-slate-800 text-slate-300 border border-slate-700">
                            <TypeIcon className="h-3.5 w-3.5" />{typeConfig.label}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <button onClick={() => setExpandedId(isExpanded ? null : item.id)} className="flex items-center gap-2 text-sm text-white hover:text-cyan-400 transition-colors text-left">
                            {isExpanded ? <ChevronUp className="h-3.5 w-3.5 flex-shrink-0" /> : <ChevronDown className="h-3.5 w-3.5 flex-shrink-0" />}
                            <span className="truncate max-w-xs">{item.displayTitle || 'Untitled'}</span>
                          </button>
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-400 font-mono">{item.sortOrder}</td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${item.active ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/30' : 'bg-slate-700/50 text-slate-500 border border-slate-600'}`}>
                            {item.active ? <Eye className="h-3 w-3" /> : <EyeOff className="h-3 w-3" />}
                            {item.active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          {scheduleStatus === 'scheduled' ? (
                            <span className="inline-flex items-center gap-1 text-xs text-amber-400"><Clock className="h-3 w-3" />Scheduled</span>
                          ) : scheduleStatus === 'expired' ? (
                            <span className="inline-flex items-center gap-1 text-xs text-red-400"><Clock className="h-3 w-3" />Expired</span>
                          ) : <span className="text-xs text-slate-600">—</span>}
                        </td>
                        <td className="px-4 py-3 text-xs text-slate-500">{formatDate(item.updatedAt || item.createdAt)}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-1">
                            <button onClick={() => handleToggle(item)} title={item.active ? 'Deactivate' : 'Activate'} className="p-1.5 rounded-md text-slate-500 hover:text-white hover:bg-slate-700 transition-colors">
                              {item.active ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                            </button>
                            <button onClick={() => openEditModal(item)} title="Edit" className="p-1.5 rounded-md text-slate-500 hover:text-cyan-400 hover:bg-slate-700 transition-colors">
                              <Pencil className="h-4 w-4" />
                            </button>
                            <button onClick={() => handleDelete(item)} title="Delete" className="p-1.5 rounded-md text-slate-500 hover:text-red-400 hover:bg-slate-700 transition-colors">
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr key={`${item.id}-expanded`} className="bg-slate-950/50">
                          <td colSpan={8} className="px-6 py-4">
                            <div className="flex gap-6">
                              <div className="flex-1">
                                <h4 className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-2">Payload JSON</h4>
                                <pre className="bg-slate-900 border border-slate-800 rounded-lg p-4 text-xs text-slate-300 overflow-x-auto max-h-64 overflow-y-auto font-mono">
                                  {(() => { try { return JSON.stringify(JSON.parse(item.payload), null, 2); } catch { return item.payload; } })()}
                                </pre>
                              </div>
                              <div className="w-48 flex-shrink-0">
                                <h4 className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-2">Details</h4>
                                <dl className="space-y-2 text-xs">
                                  <div><dt className="text-slate-600">Schema Version</dt><dd className="text-slate-300">v{item.schemaVersion}</dd></div>
                                  <div><dt className="text-slate-600">Created</dt><dd className="text-slate-300">{formatDate(item.createdAt)}</dd></div>
                                  {item.createdBy && <div><dt className="text-slate-600">Created By</dt><dd className="text-slate-300 truncate">{item.createdBy}</dd></div>}
                                  {item.publishAt && <div><dt className="text-slate-600">Publish At</dt><dd className="text-amber-400">{formatDate(item.publishAt)}</dd></div>}
                                  {item.expireAt && <div><dt className="text-slate-600">Expire At</dt><dd className="text-red-400">{formatDate(item.expireAt)}</dd></div>}
                                </dl>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalElements > 50 && (
          <div className="flex items-center justify-between mt-4">
            <button onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0} className="px-3 py-1.5 text-sm border border-slate-700 rounded-lg text-slate-400 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors">Previous</button>
            <span className="text-sm text-slate-500">Page {page + 1} of {Math.ceil(totalElements / 50)}</span>
            <button onClick={() => setPage((p) => p + 1)} disabled={(page + 1) * 50 >= totalElements} className="px-3 py-1.5 text-sm border border-slate-700 rounded-lg text-slate-400 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors">Next</button>
          </div>
        )}

        {/* ─────────────────────────────────────────────────────── */}
        {/* Create/Edit Modal                                      */}
        {/* ─────────────────────────────────────────────────────── */}
        {showModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
            <div className="bg-slate-900 border border-slate-700 rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col mx-4">
              {/* Modal Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-slate-800 flex-shrink-0">
                <h2 className="text-lg font-semibold text-white">
                  {editingItem ? 'Edit Feed Item' : 'Create Feed Item'}
                </h2>
                <button onClick={() => setShowModal(false)} className="p-1 rounded-lg text-slate-500 hover:text-white hover:bg-slate-800 transition-colors">
                  <X className="h-5 w-5" />
                </button>
              </div>

              {/* Modal Body — two-column layout */}
              <div className="flex-1 overflow-y-auto">
                <div className="flex flex-col lg:flex-row">
                  {/* Left: Payload editor */}
                  <div className="flex-1 min-w-0 px-6 py-5 space-y-5">
                    {/* Type Selector */}
                    <div>
                      <label className="block text-sm font-medium text-slate-400 mb-2">Type</label>
                      <div className="grid grid-cols-4 gap-2">
                        {FEED_TYPES.map((t) => {
                          const Icon = t.icon;
                          const isSelected = formType === t.value;
                          return (
                            <button
                              key={t.value}
                              onClick={() => {
                                setFormType(t.value);
                                if (!editingItem) {
                                  const defaultData = getDefaultPayload(t.value);
                                  setFormData(defaultData);
                                  setFormJsonRaw(JSON.stringify(defaultData, null, 2));
                                }
                              }}
                              className={`flex flex-col items-center gap-1.5 p-3 rounded-xl border-2 transition-all text-sm ${
                                isSelected ? 'border-cyan-500 bg-cyan-500/10 text-cyan-400' : 'border-slate-700 bg-slate-800/50 text-slate-400 hover:border-slate-500'
                              }`}
                            >
                              <Icon className="h-5 w-5" />{t.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* Form | JSON | Mock Tab Switcher */}
                    <div className="flex items-center border-b border-slate-800">
                      <button
                        onClick={() => handleTabSwitch('form')}
                        className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                          editorTab === 'form'
                            ? 'border-cyan-500 text-cyan-400'
                            : 'border-transparent text-slate-500 hover:text-slate-300'
                        }`}
                      >
                        <FileText className="h-4 w-4" /> Form
                      </button>
                      <button
                        onClick={() => handleTabSwitch('json')}
                        className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                          editorTab === 'json'
                            ? 'border-cyan-500 text-cyan-400'
                            : 'border-transparent text-slate-500 hover:text-slate-300'
                        }`}
                      >
                        <Code2 className="h-4 w-4" /> JSON
                      </button>
                      {!editingItem && (
                        <button
                          onClick={() => handleTabSwitch('mock')}
                          className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                            editorTab === 'mock'
                              ? 'border-amber-500 text-amber-400'
                              : 'border-transparent text-slate-500 hover:text-slate-300'
                          }`}
                        >
                          <FlaskConical className="h-4 w-4" /> Mock
                        </button>
                      )}
                    </div>

                    {/* Tab Content */}
                    {editorTab === 'form' ? (
                      <div>{renderTypeForm()}</div>
                    ) : editorTab === 'json' ? (
                      <div>
                        <textarea
                          value={formJsonRaw}
                          onChange={(e) => setFormJsonRaw(e.target.value)}
                          rows={18}
                          spellCheck={false}
                          className="w-full bg-slate-950 border border-slate-700 rounded-xl px-4 py-3 text-sm text-slate-200 font-mono focus:ring-cyan-500 focus:border-cyan-500 resize-y"
                          placeholder='{"headline": "...", ...}'
                        />
                        {formJsonRaw && (
                          <div className="mt-1 text-xs">
                            {(() => {
                              try { JSON.parse(formJsonRaw); return <span className="text-emerald-400 flex items-center gap-1"><Check className="h-3 w-3" /> Valid JSON</span>; }
                              catch (e) { return <span className="text-red-400 flex items-center gap-1"><AlertCircle className="h-3 w-3" /> Invalid JSON: {e instanceof Error ? e.message : 'Parse error'}</span>; }
                            })()}
                          </div>
                        )}
                      </div>
                    ) : (
                      /* Mock Tab — filtered by selected type */
                      (() => {
                        const filteredMocks = MOCK_SAMPLES.filter((m) => m.type === formType);
                        const typeConf = FEED_TYPES.find((t) => t.value === formType);
                        return (
                          <div className="space-y-4">
                            <div className="flex items-center justify-between">
                              <p className="text-xs text-slate-500">
                                Showing <span className="text-slate-300 font-medium">{filteredMocks.length}</span> {typeConf?.label} samples — click to load into form
                              </p>
                              <button
                                onClick={seedAllMocks}
                                disabled={isSeedingMocks}
                                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-amber-600 hover:bg-amber-500 disabled:bg-amber-600/50 text-white rounded-lg transition-colors"
                              >
                                {isSeedingMocks ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : <Zap className="h-3.5 w-3.5" />}
                                {isSeedingMocks ? 'Seeding...' : `Seed All ${MOCK_SAMPLES.length} (all types)`}
                              </button>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                              {filteredMocks.map((mock, idx) => (
                                <button
                                  key={idx}
                                  onClick={() => applyMockToForm(mock)}
                                  className="flex items-start gap-3 p-3 rounded-xl border border-slate-700 bg-slate-800/50 hover:border-cyan-500/50 hover:bg-slate-800 transition-all text-left group"
                                >
                                  <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-slate-700/50 flex items-center justify-center text-xs font-bold text-slate-400 group-hover:text-cyan-400 transition-colors">
                                    {idx + 1}
                                  </div>
                                  <div className="min-w-0 flex-1">
                                    <div className="text-sm font-medium text-white group-hover:text-cyan-400 transition-colors truncate">{mock.label}</div>
                                    <div className="text-xs text-slate-500 mt-0.5">{mock.subtitle}</div>
                                  </div>
                                </button>
                              ))}
                            </div>
                          </div>
                        );
                      })()
                    )}
                  </div>

                  {/* Right: Metadata sidebar */}
                  <div className="w-full lg:w-72 flex-shrink-0 border-t lg:border-t-0 lg:border-l border-slate-800 px-6 py-5 space-y-5 bg-slate-950/30">
                    <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Settings</h3>

                    {/* Status */}
                    <div>
                      <label className="block text-xs font-medium text-slate-500 mb-1.5">Status</label>
                      <button onClick={() => setFormActive(!formActive)} className={`w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${formActive ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-400' : 'border-slate-700 bg-slate-800 text-slate-500'}`}>
                        {formActive ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                        {formActive ? 'Active' : 'Inactive'}
                      </button>
                    </div>

                    {/* Sort Order */}
                    <div>
                      <label className="block text-xs font-medium text-slate-500 mb-1.5">Sort Order</label>
                      <input type="number" value={formSortOrder} onChange={(e) => setFormSortOrder(e.target.value)} placeholder="Auto" className={inputCls} />
                      <p className="text-xs text-slate-600 mt-1">Lower = higher position</p>
                    </div>

                    <hr className="border-slate-800" />

                    <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Scheduling</h3>

                    {/* Publish At */}
                    <div>
                      <label className="block text-xs font-medium text-slate-500 mb-1.5">
                        <Clock className="h-3 w-3 inline mr-1" />Publish At
                      </label>
                      <input type="datetime-local" value={formPublishAt} onChange={(e) => setFormPublishAt(e.target.value)} className={inputCls} />
                    </div>

                    {/* Expire At */}
                    <div>
                      <label className="block text-xs font-medium text-slate-500 mb-1.5">
                        <Clock className="h-3 w-3 inline mr-1" />Expire At
                      </label>
                      <input type="datetime-local" value={formExpireAt} onChange={(e) => setFormExpireAt(e.target.value)} className={inputCls} />
                    </div>

                    <hr className="border-slate-800" />

                    <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                      <ImageIcon className="h-3 w-3 inline mr-1" />Image
                    </h3>

                    {/* Image preview or upload zone */}
                    {(currentImageUrl || imagePreview) ? (
                      <div className="space-y-2">
                        <div className="relative rounded-lg overflow-hidden border border-slate-700">
                          <img
                            src={imagePreview || currentImageUrl || ''}
                            alt="Feed item"
                            className="w-full h-32 object-cover"
                          />
                          {isUploading && (
                            <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                              <div className="text-center">
                                <RefreshCw className="h-5 w-5 animate-spin text-cyan-400 mx-auto mb-1" />
                                <p className="text-xs text-slate-300">{uploadProgress}</p>
                              </div>
                            </div>
                          )}
                        </div>
                        <div className="flex gap-2">
                          <label className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 text-xs text-slate-400 border border-slate-700 rounded-lg hover:border-slate-500 hover:text-white cursor-pointer transition-colors">
                            <Upload className="h-3 w-3" /> Replace
                            <input type="file" accept="image/jpeg,image/png,image/webp" onChange={handleFileInput} className="hidden" />
                          </label>
                          <button
                            onClick={handleRemoveImage}
                            className="flex items-center justify-center gap-1 px-2 py-1.5 text-xs text-red-400 border border-red-500/30 rounded-lg hover:border-red-500/50 hover:bg-red-500/5 transition-colors"
                          >
                            <X className="h-3 w-3" /> Remove
                          </button>
                        </div>
                      </div>
                    ) : (
                      <label
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={handleFileDrop}
                        className="flex flex-col items-center justify-center gap-2 p-4 border-2 border-dashed border-slate-700 rounded-xl hover:border-cyan-500/40 hover:bg-slate-800/30 cursor-pointer transition-colors"
                      >
                        <Upload className="h-5 w-5 text-slate-500" />
                        <span className="text-xs text-slate-500 text-center">Drop image or click to upload</span>
                        <span className="text-xs text-slate-600">JPEG, PNG, WebP — max 5 MB</span>
                        <input type="file" accept="image/jpeg,image/png,image/webp" onChange={handleFileInput} className="hidden" />
                      </label>
                    )}
                  </div>
                </div>
              </div>

              {/* Submit Error */}
              {submitError && (
                <div className="mx-6 mb-0 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm flex items-start gap-2">
                  <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" /><div>{submitError}</div>
                </div>
              )}

              {/* Modal Footer */}
              <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-800 flex-shrink-0">
                <button onClick={() => setShowModal(false)} className="px-4 py-2 text-sm text-slate-400 hover:text-white transition-colors">Cancel</button>
                <button onClick={handleSave} disabled={isSubmitting} className="flex items-center gap-2 px-5 py-2 bg-cyan-600 hover:bg-cyan-500 disabled:bg-cyan-600/50 text-white text-sm font-medium rounded-lg transition-colors">
                  {isSubmitting ? (<><RefreshCw className="h-4 w-4 animate-spin" />Saving...</>) : (<><Check className="h-4 w-4" />{editingItem ? 'Update' : 'Create'}</>)}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
